import skins from '../skinsV2.json';

class SkinOptionGenerator {
  constructor(windowSize = 10) {
    this.skins = skins;
    this.windowSize = windowSize;
    // Use a real circular-queue behavior via queue+set
    this.recentChampionQueue = [];
    this.recentChampionSet = new Set();
    this.pickedSkins = new Set(); // Track picked skins by unique identifier
    this.availableIndices = Array.from({ length: this.skins.length }, (_, i) => i);
    this.currentIndex = 0 // For in-order picking
    // Guards to avoid double-increment in React Strict Mode (double effect invocation in dev)
    this._inOrderGuardForward = false;
    this._inOrderGuardReverse = false;
  }

  // Deterministic daily picker based on a date string seed (e.g., '2025-08-22')
  getDailySkinForDate(dateSeed) {
    const hash = this._hashString(dateSeed);
    const idx = Math.abs(hash) % this.skins.length;
    return this.skins[idx];
  }

  getDailySkinForDateAndRound(dateSeed, roundIndex) {
    // Deterministic pick avoiding repeats from the previous N days across all rounds
    const selections = this._getDailySkinsAvoidingRecent(dateSeed, 3, 30);
    const pick = selections[roundIndex % selections.length];
    return pick;
  }

  getSkinByLoc(loc) {
    return this.skins.find(s => s.loc === loc) || null;
  }

  // INTERNALS
  _formatYMDFromUTCDate(d) {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }

  _dateMinusDays(ymd, days) {
    // Parse as UTC midnight to avoid TZ drift
    const d = new Date(`${ymd}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - days);
    return this._formatYMDFromUTCDate(d);
  }

  _baseIndexFor(dateSeed, roundIndex, attempt) {
    const salt = `${dateSeed}#${roundIndex}#${attempt}`;
    const hash = this._hashString(salt);
    return Math.abs(hash) % this.skins.length;
  }

  _getDailySkinsAvoidingRecent(dateSeed, rounds = 3, lookbackDays = 30) {
    // Build recent set using base, attempt=0 selection for prior days and all rounds
    const recentIds = new Set();
    for (let back = 1; back <= lookbackDays; back++) {
      const prev = this._dateMinusDays(dateSeed, back);
      for (let r = 0; r < rounds; r++) {
        const idx = this._baseIndexFor(prev, r, 0);
        const skin = this.skins[idx];
        recentIds.add(this.getSkinUniqueId(skin));
      }
    }
    // Now pick today's rounds, avoiding any in recentIds and duplicates within today
    const todayPicks = [];
    const todayIds = new Set();
    for (let r = 0; r < rounds; r++) {
      let chosen = null;
      for (let attempt = 0; attempt < 1000; attempt++) {
        const idx = this._baseIndexFor(dateSeed, r, attempt);
        const skin = this.skins[idx];
        const id = this.getSkinUniqueId(skin);
        if (!recentIds.has(id) && !todayIds.has(id)) {
          chosen = skin;
          todayIds.add(id);
          break;
        }
      }
      // Fallback to base pick if we somehow couldn't find a unique one in attempts
      if (!chosen) {
        const idx = this._baseIndexFor(dateSeed, r, 0);
        chosen = this.skins[idx];
        todayIds.add(this.getSkinUniqueId(chosen));
      }
      todayPicks.push(chosen);
    }
    return todayPicks;
  }

  getSkinUniqueId(skin) {
    if (!skin) return '';
    return `${skin.champion}_${skin.name}`;
  }

  _hashString(str) {
    // Simple 32-bit FNV-1a hash for stability across sessions
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return h | 0; // force signed 32-bit
  }

  getNextSkin() {
    // Build the pool excluding recently used champions and already picked skins
    let filteredIndices = this.availableIndices.filter(idx => {
      const skin = this.skins[idx];
      const champ = skin.champion;
      const skinId = `${skin.champion}_${skin.name}`; // Unique identifier for each skin
      return !this.recentChampionSet.has(champ) && !this.pickedSkins.has(skinId);
    });

    if (filteredIndices.length === 0) {
      // If everything is blocked, first try re-introducing the oldest recent champion(s)
      // one-by-one to maintain circular-queue behavior.
      while (filteredIndices.length === 0 && this.recentChampionQueue.length > 0) {
        const oldest = this.recentChampionQueue.shift();
        this.recentChampionSet.delete(oldest);
        filteredIndices = this.availableIndices.filter(idx => {
          const skin = this.skins[idx];
          const champ = skin.champion;
          const skinId = `${skin.champion}_${skin.name}`;
          return !this.recentChampionSet.has(champ) && !this.pickedSkins.has(skinId);
        });
      }

      // If still empty, then all skins have likely been picked. Reset that set.
      if (filteredIndices.length === 0) {
        this.pickedSkins.clear();
        return this.getNextSkin();
      }
    }
    
    // Pick a random index from filteredIndices
    const pickIdx = filteredIndices[Math.floor(Math.random() * filteredIndices.length)];
    const picked = this.skins[pickIdx];
    
    // Enqueue the champion and evict the oldest if exceeding the window size
    this.recentChampionQueue.push(picked.champion);
    this.recentChampionSet.add(picked.champion);
    if (this.recentChampionQueue.length > this.windowSize) {
      const removed = this.recentChampionQueue.shift();
      this.recentChampionSet.delete(removed);
    }
    
    // Mark this skin as picked
    const skinId = `${picked.champion}_${picked.name}`;
    this.pickedSkins.add(skinId);
    
    return picked;
  }

  getNextSkinInOrder(reverse=false) {
    const guardKey = reverse ? '_inOrderGuardReverse' : '_inOrderGuardForward';
    const idx = reverse
      ? this.skins.length - 1 - this.currentIndex
      : this.currentIndex;
    const result = this.skins[idx];

    // Schedule a single increment for this render tick; prevents double-increment in Strict Mode
    if (!this[guardKey]) {
      this[guardKey] = true;
      setTimeout(() => {
        this.currentIndex = Math.min(this.currentIndex + 1, this.skins.length);
        this[guardKey] = false;
      }, 0);
    }

    return result;
  }

  getTotalSkins() {
    return this.skins.length;
  }

  getSkinByName(skinName) {
    return this.skins.find(skin => skin.name === skinName) || null;
  }

  getSkinsByChampion(championName) {
    return this.skins.filter(skin => skin.champion.toLowerCase() === championName.toLowerCase());
  }
}

export default SkinOptionGenerator;
